import type { SpecializationCount } from '../services/professional-public.service';

interface SpecializationCardProps {
  specializations: SpecializationCount[];
  /** Currently active specialization name (from the URL filter), if any. */
  active?: string;
  /** Called with a spec name to filter, or undefined to clear. */
  onSelect: (name?: string) => void;
  /** Pin to a fixed pixel height (matches the pro cards); list scrolls inside. */
  height?: number;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Specializations quick-pick rendered as a single CARD that drops into the
 * Top7 grid as the slot right after rank 7 (the "top 8" cell). It matches the
 * FeaturedCard orange-gradient language, stretches to the grid row height
 * (h-full) and scrolls its list internally so height/width never grow.
 * Filtering goes through the parent's onSelect (updateSearchParams) — never a
 * raw <Link> — so param-merge + pagination reset stay intact.
 * ────────────────────────────────────────────────────────────────────────── */
export function SpecializationCard({ specializations, active, onSelect, height }: SpecializationCardProps) {
  return (
    <div
      style={height != null ? { height } : undefined}
      className="flex h-full min-h-[360px] flex-col overflow-hidden rounded-xl border border-brand-orange/25 bg-gradient-to-br from-brand-orange/[0.06] to-transparent backdrop-blur-sm"
    >
      {/* Header (non-scrolling) */}
      <div className="flex items-start justify-between gap-2 border-b border-brand-orange/10 px-4 py-3">
        <div>
          <h3 className="text-sm font-bold text-brand-navy">Specializzazioni</h3>
          <p className="text-[11px] text-neutral-400">{specializations.length} categorie</p>
        </div>
        {active && (
          <button
            type="button"
            onClick={() => onSelect(undefined)}
            className="shrink-0 text-[11px] font-medium text-brand-orange/70 underline underline-offset-2 hover:text-brand-orange"
          >
            Azzera
          </button>
        )}
      </div>

      {/* Scroll region */}
      <nav aria-label="Specializzazioni" className="flex-1 overflow-y-auto p-2 [scrollbar-width:thin]">
        <button
          type="button"
          onClick={() => onSelect(undefined)}
          aria-current={!active}
          className={`mb-1 flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange/40 ${
            !active
              ? 'bg-brand-teal/15 font-semibold text-brand-teal-400'
              : 'text-neutral-600 hover:bg-brand-orange/[0.06] hover:text-brand-navy'
          }`}
        >
          Tutte
        </button>
        {specializations.map(({ name, count }) => {
          const isActive = active === name;
          const disabled = count === 0;
          return (
            <button
              key={name}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(isActive ? undefined : name)}
              aria-current={isActive}
              title={name}
              className={`mb-1 flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange/40 ${
                disabled
                  ? 'cursor-not-allowed opacity-40'
                  : isActive
                    ? 'bg-brand-teal/15 font-semibold text-brand-teal-400'
                    : 'text-neutral-600 hover:bg-brand-orange/[0.06] hover:text-brand-navy'
              }`}
            >
              <span className="min-w-0 flex-1 truncate">{name}</span>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums ${
                  isActive ? 'bg-brand-teal text-white' : 'bg-brand-teal/10 text-brand-teal-400/80'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
